import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

/**
 * Robust wrapper around React.lazy that handles chunk load failures.
 *
 * In Single Page Applications, when a new production build is deployed with new chunk hashes,
 * existing user sessions or cached pages may request older chunk filenames that return 404
 * ("Failed to fetch dynamically imported module").
 *
 * This utility detects such failures and automatically triggers a window reload once to
 * fetch the fresh index.html and modern chunks, avoiding broken screen states for the user.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    const sessionKey = 'nttd_chunk_reload_' + window.location.pathname

    try {
      const module = await factory()
      // If successfully loaded, clear any previous reload flag for this route
      sessionStorage.removeItem(sessionKey)
      return module
    } catch (error: any) {
      const msg = error?.message || ''
      const isChunkError =
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('error loading dynamically imported module') ||
        msg.includes('Loading chunk') ||
        error?.name === 'ChunkLoadError'

      if (isChunkError) {
        const hasAlreadyReloaded = sessionStorage.getItem(sessionKey)
        const lastReloadTimestamp = sessionStorage.getItem('nttd_last_chunk_reload_ts')
        const now = Date.now()

        // Allow at most one automatic reload per route within 15 seconds to prevent reload loops
        const recentlyReloaded = lastReloadTimestamp && now - parseInt(lastReloadTimestamp, 10) < 15000

        if (!hasAlreadyReloaded && !recentlyReloaded) {
          sessionStorage.setItem(sessionKey, 'true')
          sessionStorage.setItem('nttd_last_chunk_reload_ts', now.toString())
          console.warn('[lazyWithRetry] Stale chunk detected. Refreshing page to load latest build assets...')
          window.location.reload()
          // Return a hanging promise while the page reloads
          return new Promise<{ default: T }>(() => {})
        }
      }

      throw error
    }
  })
}
